import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Card } from '../ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../ui/alert'
import { supabase } from '../../lib/supabase'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../lib/auth.context'

interface TOTPEnrollmentProps {
  onComplete?: () => void
  redirectTo?: string
  /** Renders the bare card with no split-screen shell, for use inside a dialog. */
  isEmbedded?: boolean
}

export function TOTPEnrollment({ onComplete, redirectTo, isEmbedded = false }: TOTPEnrollmentProps) {
  const [qrCode, setQrCode] = useState<string>('')
  const [secret, setSecret] = useState<string>('')
  const [verifyCode, setVerifyCode] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [factorId, setFactorId] = useState<string>('')
  const [setupError, setSetupError] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t } = useTranslation()
  const { setIsTOTPVerified } = useAuth()

  useEffect(() => {
    const setupTOTP = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user?.email) throw new Error('User email not found')

        // First, check for existing factors and clean them up
        const { data: existingFactors } = await supabase.auth.mfa.listFactors()
        console.log('[TOTPEnrollment] Existing factors:', existingFactors)

        // Remove any existing TOTP factors (both verified and unverified) to avoid conflicts
        const allTotpFactors = existingFactors?.totp || []
        const allFactors = existingFactors?.all || []

        // Check all factors, not just TOTP array, as factors might be in different states
        const totpFactorsToRemove = allFactors.filter(factor => factor.factor_type === 'totp')

        for (const factor of totpFactorsToRemove) {
          console.log('[TOTPEnrollment] Removing existing TOTP factor:', factor.id, 'status:', factor.status)
          try {
            await supabase.auth.mfa.unenroll({ factorId: factor.id })
            console.log('[TOTPEnrollment] Successfully removed factor:', factor.id)
          } catch (unenrollError) {
            console.warn('[TOTPEnrollment] Failed to remove factor:', factor.id, unenrollError)
            // Continue anyway, the enrollment might still work
          }
        }

        console.log('[TOTPEnrollment] Starting enrollment process')
        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          issuer: 'FanFair Admin',
          friendlyName: `${t('title')} - ${user.email}`
        })

        if (error) {
          console.error('[TOTPEnrollment] Enrollment failed:', error)
          throw error
        }

        console.log('[TOTPEnrollment] Enrollment successful:', data)
        if (data.totp) {
          setQrCode(data.totp.qr_code)
          setSecret(data.totp.secret)
          setFactorId(data.id)
          console.log('[TOTPEnrollment] QR code and secret set successfully')
        } else {
          console.error('[TOTPEnrollment] No TOTP data in enrollment response')
          throw new Error('No TOTP data received from enrollment')
        }
      } catch (error: any) {
        console.error('[TOTPEnrollment] Setup failed:', error)
        let errorMessage = error.message || 'Failed to setup TOTP'

        // Handle specific error cases
        if (error.message?.includes('already exists') || error.status === 422) {
          errorMessage = 'A TOTP device is already being set up. Please try the "Reset and Retry" button below.'
          setSetupError(true)
        }

        setFormError(errorMessage)
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        })
      }
    }

    setupTOTP()
  }, [toast, t])

  const handleReset = async () => {
    console.log('[TOTPEnrollment] Manual reset triggered')
    setFormError('')
    setSetupError(false)
    setQrCode('')
    setSecret('')
    setFactorId('')

    // Force a fresh setup
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.email) throw new Error('User email not found')

      // More aggressive cleanup - get all factors and remove TOTP ones
      const { data: existingFactors } = await supabase.auth.mfa.listFactors()
      console.log('[TOTPEnrollment] Reset - existing factors:', existingFactors)

      const allFactors = existingFactors?.all || []
      const totpFactorsToRemove = allFactors.filter(factor => factor.factor_type === 'totp')

      for (const factor of totpFactorsToRemove) {
        console.log('[TOTPEnrollment] Reset - removing factor:', factor.id)
        try {
          await supabase.auth.mfa.unenroll({ factorId: factor.id })
        } catch (err) {
          console.warn('[TOTPEnrollment] Reset - failed to remove factor:', err)
        }
      }

      // Wait a moment then try enrollment again
      setTimeout(async () => {
        try {
          const { data, error } = await supabase.auth.mfa.enroll({
            factorType: 'totp',
            issuer: 'FanFair Admin',
            friendlyName: `${t('title')} - ${user.email}`
          })

          if (error) throw error

          if (data.totp) {
            setQrCode(data.totp.qr_code)
            setSecret(data.totp.secret)
            setFactorId(data.id)
            console.log('[TOTPEnrollment] Reset successful')
          }
        } catch (resetError: any) {
          console.error('[TOTPEnrollment] Reset failed:', resetError)
          setFormError(resetError.message || 'Reset failed')
        }
      }, 1000)
    } catch (error: any) {
      console.error('[TOTPEnrollment] Reset error:', error)
      setFormError(error.message || 'Reset failed')
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsLoading(true)

    try {
      const { data: challenge, error } = await supabase.auth.mfa.challenge({
        factorId: factorId
      })

      if (error) throw error

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challenge.id,
        code: verifyCode
      })

      if (verifyError) throw verifyError

      toast({
        title: 'Success',
        description: 'TOTP device enrolled successfully',
      })

      // Refresh the session to ensure we have the latest token
      const { data: { session: newSession }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError

      // Ensure we have a valid session before redirecting
      if (!newSession?.access_token) {
        throw new Error('Failed to initialize session after TOTP enrollment')
      }

      // Set TOTP verification status after successful enrollment
      console.log('[TOTPEnrollment] Setting TOTP verification to true after successful enrollment')
      setIsTOTPVerified(true)

      if (onComplete) {
        onComplete()
      }

      // Show success message and redirect to admin dashboard
      toast({
        title: 'Setup Complete',
        description: 'Your account has been set up successfully. Redirecting to dashboard...',
      })

      // Short delay to show the success message
      setTimeout(() => {
        if (redirectTo) {
          // Check if it's an external URL (starts with http:// or https://)
          if (redirectTo.startsWith('http://') || redirectTo.startsWith('https://')) {
            window.location.href = redirectTo
          } else {
            navigate(redirectTo)
          }
        } else {
          navigate('/admin/dashboard')
        }
      }, 1500)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify TOTP'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  // The split-screen shell sits on a dark slate panel, so the muted copy needs
  // different treatment than it does on the embedded (dialog) card.
  const mutedTextClass = isEmbedded ? 'text-gray-600' : 'text-slate-400'
  const bodyTextClass = isEmbedded ? getThemeClass('components.text.body') : 'text-slate-300'
  const secretClass = isEmbedded ? 'bg-gray-100' : 'bg-slate-900 text-slate-200'
  const inputClass = isEmbedded
    ? getThemeClass('components.input.base')
    : 'bg-slate-900 border-slate-700 text-white placeholder:text-slate-500'

  const enrollmentBody = (
    <div className="space-y-4">
      <div className="text-center">
        {qrCode ? (
          <div className="mb-4">
            <img src={qrCode} alt="QR Code" className="mx-auto" />
          </div>
        ) : (
          <div className="mb-4 p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-2"></div>
            <p className={`text-sm ${mutedTextClass}`}>Generating QR code...</p>
          </div>
        )}
        {secret && (
          <div className="mb-4">
            <p className={`text-sm ${mutedTextClass}`}>Manual entry code:</p>
            <code className={`block p-2 rounded ${secretClass}`}>{secret}</code>
          </div>
        )}
        {qrCode && (
          <>
            <p className={`mb-4 ${bodyTextClass}`}>
              Use the Google or Microsoft Authenticator app to scan the QR code with your phone. If you don't have an authenticator app, Download one from your phone's app store.
            </p>
            <div className="flex justify-center items-center gap-6 mb-4">
              <img src="/GoogleAuthenticator.png" alt="Google Authenticator" className="h-12 w-auto" />
              <img src="/MicrosoftAuthenticator.png" alt="Microsoft Authenticator" className="h-12 w-auto" />
            </div>
          </>
        )}
      </div>
      <form onSubmit={handleVerify} className="space-y-4">
        <div>
          <Input
            type="text"
            placeholder="Enter verification code"
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value)}
            required
            disabled={isLoading}
            className={inputClass}
          />
        </div>
        {formError && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        <Button
          type="submit"
          className={`w-full ${getThemeClass('components.button.primary')}`}
          disabled={isLoading || !factorId}
        >
          {isLoading ? 'Verifying...' : 'Verify and Complete Setup'}
        </Button>
        {setupError && (
          <Button
            type="button"
            variant="outline"
            className="w-full mt-2"
            onClick={handleReset}
            disabled={isLoading}
          >
            Reset and Retry
          </Button>
        )}
      </form>
    </div>
  )

  if (isEmbedded) {
    return (
      <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
        <h1 className={`mb-6 text-xl font-bold ${getThemeClass('components.text.heading')} text-center`}>
          Set Up Two-Factor Authentication
        </h1>
        {enrollmentBody}
      </Card>
    )
  }

  return (
    <div className="flex min-h-[500px]">
      {/* Left Panel - Marketing Content */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-l-2xl">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(/fanfairpartnerlogin.jpg)` }}
        />
        {/* Header */}
        <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
          <div className="flex items-center">
            <img src="/fanfairreversed.png" alt="FanFair" className="h-8" />
          </div>
        </div>
        {/* Content */}
        <div className="relative z-10 flex items-center justify-center p-12">
          <div className="text-center text-white max-w-md bg-black/60 p-10 border border-4 border-[#868686]/10 rounded-3xl">
            <h1 className="text-3xl font-bold mb-4 text-white">Capturing Moments, Creating Memories</h1>
            <p className="text-lg opacity-90 mb-8 text-white">
              Secure your Admin Portal account with two-factor authentication.
            </p>
            {/* Slide indicators */}
            <div className="flex justify-center space-x-2">
              <div className="w-8 h-1 bg-white rounded-full"></div>
              <div className="w-1 h-1 bg-white/40 rounded-full"></div>
              <div className="w-1 h-1 bg-white/40 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
      {/* Right Panel - Enrollment Form */}
      <div className="w-full lg:w-1/2 bg-slate-800 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm space-y-8">
          {/* Mobile Header */}
          <div className="lg:hidden text-center">
            <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto" />
          </div>
          <h1 className="text-2xl font-medium text-white text-center">Set Up Two-Factor Authentication</h1>
          {enrollmentBody}
        </div>
      </div>
    </div>
  )
}
